// Tipos de funções de usuário
export enum UserRole {
    ADMIN = 'ADMIN',
    FINANCIAL = 'FINANCIAL',
    PHOTOGRAPHER = 'PHOTOGRAPHER',
    INFLUENCER = 'INFLUENCER',
    MARKETING = 'MARKETING',
    SUPPORT = 'SUPPORT',
}

// Enum para todas as permissões disponíveis
export enum Permission {
    // Analytics
    ANALYTICS_VIEW = 'analytics:view',
    ANALYTICS_EDIT = 'analytics:edit',

    // Usuários
    USERS_VIEW = 'users:view',
    USERS_EDIT = 'users:edit',

    // Packs
    PACKS_VIEW = 'packs:view',
    PACKS_EDIT = 'packs:edit',
    PACKS_DELETE = 'packs:delete',
    PACKS_CREATE = 'packs:create',

    // Presets
    PRESETS_VIEW = 'presets:view',
    PRESETS_EDIT = 'presets:edit',
    PRESETS_DELETE = 'presets:delete',
    PRESETS_CREATE = 'presets:create',

    // Configurações
    SETTINGS_VIEW = 'settings:view',
    SETTINGS_EDIT = 'settings:edit',

    // Tags
    TAGS_VIEW = 'tags:view',
    TAGS_EDIT = 'tags:edit',
    TAGS_DELETE = 'tags:delete',
    TAGS_CREATE = 'tags:create',
    // Conteúdos de conteúdo
    LEARNING_VIEW = 'learning:view',
    LEARNING_EDIT = 'learning:edit',
    LEARNING_DELETE = 'learning:delete',
    LEARNING_CREATE = 'learning:create',
    // Categorias
    CATEGORIES_VIEW = 'categories:view',
    CATEGORIES_EDIT = 'categories:edit',
    CATEGORIES_DELETE = 'categories:delete',

    // Templates PSD
    TEMPLATES_VIEW = 'templates:view',
    TEMPLATES_EDIT = 'templates:edit',
    TEMPLATES_DELETE = 'templates:delete',

    // Highlights/Seções
    HIGHLIGHTS_VIEW = 'highlights:view',
    HIGHLIGHTS_EDIT = 'highlights:edit',

    // Contratos
    CONTRACTS_VIEW = 'contracts:view',
    CONTRACTS_EDIT = 'contracts:edit',
    CONTRACTS_DELETE = 'contracts:delete',
    CONTRACTS_CREATE = 'contracts:create',

    // Recebíveis
    RECEIVABLES_VIEW = 'receivables:view',
    RECEIVABLES_EDIT = 'receivables:edit',
    RECEIVABLES_DELETE = 'receivables:delete',

    // Pagamentos
    PAYMENTS_VIEW = 'payments:view',
    PAYMENTS_EDIT = 'payments:edit',
    PAYMENTS_DELETE = 'payments:delete',
    PAYMENTS_CREATE = 'payments:create',

    // Notificações em massa
    NOTIFICATIONS_VIEW = 'notifications:view',
    NOTIFICATIONS_EDIT = 'notifications:edit',
}

// Grupos de permissões para facilitar o gerenciamento
export const PermissionGroups = {
    ANALYTICS: [Permission.ANALYTICS_VIEW, Permission.ANALYTICS_EDIT],
    USERS: [Permission.USERS_VIEW, Permission.USERS_EDIT],
    PACKS: [Permission.PACKS_VIEW, Permission.PACKS_EDIT, Permission.PACKS_DELETE, Permission.PACKS_CREATE],
    PRESETS: [Permission.PRESETS_VIEW, Permission.PRESETS_EDIT, Permission.PRESETS_DELETE, Permission.PRESETS_CREATE],
    SETTINGS: [Permission.SETTINGS_VIEW, Permission.SETTINGS_EDIT],
    TAGS: [Permission.TAGS_VIEW, Permission.TAGS_EDIT, Permission.TAGS_DELETE, Permission.TAGS_CREATE],
    LEARNING: [Permission.LEARNING_VIEW, Permission.LEARNING_EDIT, Permission.LEARNING_DELETE, Permission.LEARNING_CREATE],
    CATEGORIES: [Permission.CATEGORIES_VIEW, Permission.CATEGORIES_EDIT, Permission.CATEGORIES_DELETE],
    TEMPLATES: [Permission.TEMPLATES_VIEW, Permission.TEMPLATES_EDIT, Permission.TEMPLATES_DELETE],
    HIGHLIGHTS: [Permission.HIGHLIGHTS_VIEW, Permission.HIGHLIGHTS_EDIT],
    CONTRACTS: [Permission.CONTRACTS_VIEW, Permission.CONTRACTS_EDIT, Permission.CONTRACTS_DELETE, Permission.CONTRACTS_CREATE],
    RECEIVABLES: [Permission.RECEIVABLES_VIEW, Permission.RECEIVABLES_EDIT, Permission.RECEIVABLES_DELETE],
    PAYMENTS: [Permission.PAYMENTS_VIEW, Permission.PAYMENTS_EDIT, Permission.PAYMENTS_DELETE, Permission.PAYMENTS_CREATE],
    NOTIFICATIONS: [Permission.NOTIFICATIONS_VIEW, Permission.NOTIFICATIONS_EDIT],
} as const;

// Tipo para garantir type safety nas permissões
export type PermissionType = keyof typeof PermissionGroups;

// Mapeamento de funções para permissões usando tipos seguros
type RolePermissions = {
    [key in UserRole]: Permission[];
};

// Definição das permissões por função usando grupos
const permissions: RolePermissions = {
    [UserRole.ADMIN]: [
        // Admin tem acesso total a todos os grupos
        ...PermissionGroups.ANALYTICS,
        ...PermissionGroups.USERS,
        ...PermissionGroups.PACKS,
        ...PermissionGroups.PRESETS,
        ...PermissionGroups.SETTINGS,
        ...PermissionGroups.TAGS,
        ...PermissionGroups.LEARNING,
        ...PermissionGroups.CATEGORIES,
        ...PermissionGroups.TEMPLATES,
        ...PermissionGroups.HIGHLIGHTS,
        ...PermissionGroups.CONTRACTS,
        ...PermissionGroups.RECEIVABLES,
        ...PermissionGroups.PAYMENTS,
        ...PermissionGroups.NOTIFICATIONS,
    ],
    [UserRole.FINANCIAL]: [
        ...PermissionGroups.RECEIVABLES,
        ...PermissionGroups.PAYMENTS,
    ],
    [UserRole.INFLUENCER]: [
        // Influencer tem acesso limitado - apenas visualização
        Permission.ANALYTICS_VIEW,
        Permission.PACKS_VIEW, // Apenas visualização, não edição
    ],
    [UserRole.PHOTOGRAPHER]: [
        // Photographer tem acesso limitado - apenas visualização
        Permission.ANALYTICS_VIEW,
        Permission.PACKS_VIEW, // Apenas visualização, não edição
    ],
    [UserRole.MARKETING]: [
        ...PermissionGroups.ANALYTICS,
        ...PermissionGroups.TEMPLATES,
        ...PermissionGroups.CATEGORIES,
    ],
    [UserRole.SUPPORT]: [
     
    ],
};

// Função para verificar se um usuário tem uma permissão específica
export const hasPermission = (userRole: UserRole, permission: Permission): boolean => {
    if (!userRole) return false;
    return permissions[userRole]?.includes(permission) || false;
};

// Função para verificar se o usuário tem pelo menos uma das permissões fornecidas
export const hasAnyPermission = (userRole: UserRole, requiredPermissions: Permission[]): boolean => {
    if (!userRole) return false;
    return requiredPermissions.some(permission => hasPermission(userRole, permission));
};

// Função para verificar se o usuário tem todas as permissões fornecidas
export const hasAllPermissions = (userRole: UserRole, requiredPermissions: Permission[]): boolean => {
    if (!userRole) return false;
    return requiredPermissions.every(permission => hasPermission(userRole, permission));
};

// Função para verificar se o usuário tem permissões de um grupo específico
export const hasPermissionGroup = (userRole: UserRole, group: PermissionType): boolean => {
    if (!userRole) return false;
    const groupPermissions = PermissionGroups[group];
    return groupPermissions.every(permission => hasPermission(userRole, permission));
};

// Função para obter todas as permissões de um usuário
export const getUserPermissions = (userRole: UserRole): Permission[] => {
    if (!userRole) return [];
    return permissions[userRole] || [];
};

// Função para obter permissões de um grupo específico que o usuário possui
export const getUserPermissionsFromGroup = (userRole: UserRole, group: PermissionType): Permission[] => {
    if (!userRole) return [];
    const groupPermissions = PermissionGroups[group];
    return groupPermissions.filter(permission => hasPermission(userRole, permission));
};

// Função para validar se uma permissão existe
export const isValidPermission = (permission: string): permission is Permission => {
    return Object.values(Permission).includes(permission as Permission);
};

// Função para obter todas as permissões disponíveis
export const getAllPermissions = (): Permission[] => {
    return Object.values(Permission);
};

// Função para obter todas as permissões de um grupo
export const getGroupPermissions = (group: PermissionType): Permission[] => {
    return [...PermissionGroups[group]];
};

// Função para verificar se um usuário é admin
export const isAdmin = (userRole: UserRole): boolean => {
    return userRole === UserRole.ADMIN;
};

// Função para verificar se um usuário é creator (photographer ou influencer)
export const isCreator = (userRole: UserRole): boolean => {
    return userRole === UserRole.PHOTOGRAPHER || userRole === UserRole.INFLUENCER;
};

export default permissions;
